/**
 * VoiceController – verbindet REST (docs/API.md 4.6), LiveKit (livekit-client) und den globalen Voice-Store.
 * Genau eine Instanz pro Tab (`voiceController`), damit die Verbindung beim Wechsel der Ansicht bestehen bleibt.
 * Abhängigkeiten sind injizierbar (Tests mit gemocktem livekit-client und Fake-API).
 */
import type { CurrentUserDto, UserDto, VoiceJoinResponseDto, VoiceStateDto, ChannelDto } from '@lagerfeuer/shared';
import {
  DisconnectReason,
  Room,
  RoomEvent,
  Track,
  type LocalParticipant,
  type Participant,
  type RemoteParticipant,
  type RemoteTrack,
  type RoomConnectOptions,
  type RoomOptions,
} from 'livekit-client';
import { api } from '../../lib/api';
import { useAuthStore } from '../../store/auth';
import {
  applyVoiceStateUpdate,
  buildRoomOptions,
  clampVolume,
  diffVoiceState,
  isUserCancel,
  joinErrorMessage,
  ownVoiceState,
  playbackMuted,
  publishRightsFrom,
  voiceMoveTarget,
  loadDevicePrefs,
  loadScreenPreset,
  loadVolumes,
  qualityLevel,
  rtcConfigFrom,
  safeStorage,
  saveDevicePref,
  saveScreenPreset,
  saveVolume,
  screenPresetChoice,
  screenShareCaptureOptions,
  screenSharePublishOptions,
  shortUserLabel,
  type ReportedVoiceState,
  type VoiceDeviceKind,
  type VoiceStateUpdate,
} from './logic';
import { debugState, snapshotRoom, type VoiceDebugState } from './snapshot';
import { connectionResetPatch, useVoiceStore, type DeviceOption, type VoiceStoreState } from './store';

type RequestInit2 = RequestInit & { json?: unknown };

export interface VoiceControllerDeps {
  request: <T>(path: string, init?: RequestInit2) => Promise<T>;
  createRoom: (options: RoomOptions) => Room;
  listDevices: (kind: VoiceDeviceKind) => Promise<MediaDeviceInfo[]>;
  storage: () => Storage | null;
  selfId: () => string | null;
}

/**
 * Wartezeit nach LiveKit-`Disconnected(PARTICIPANT_REMOVED)` auf das eigene VOICE_STATE_UPDATE: Beim Verschieben durch
 * einen Moderator entfernt der Server die Sitzung meist schon aus dem alten LiveKit-Raum, bevor das Socket-Ereignis
 * ankommt. Innerhalb dieser Frist wird das Update noch ausgewertet (`moved: true` → Zielkanal automatisch beitreten).
 */
export const MOVE_GRACE_MS = 1_500;

/** Hinweis, wenn derselbe Nutzer auf einem anderen Gerät/Tab einem (anderen) Sprachkanal beigetreten ist. */
export const JOINED_ELSEWHERE_MESSAGE = 'Du bist an anderer Stelle einem Sprachkanal beigetreten.';

const DEVICE_FALLBACK_LABEL: Record<VoiceDeviceKind, string> = {
  audioinput: 'Mikrofon',
  videoinput: 'Kamera',
  audiooutput: 'Lautsprecher',
};

function disconnectMessage(reason: DisconnectReason | undefined): string {
  switch (reason) {
    case DisconnectReason.DUPLICATE_IDENTITY:
      return 'Du bist dem Sprachkanal an anderer Stelle beigetreten.';
    case DisconnectReason.PARTICIPANT_REMOVED:
      return 'Du wurdest aus dem Sprachkanal getrennt.';
    case DisconnectReason.ROOM_DELETED:
      return 'Der Sprachkanal wurde geschlossen.';
    default:
      return 'Die Verbindung zum Sprachkanal ist abgebrochen.';
  }
}

export class VoiceController {
  private room: Room | null = null;
  /** Erhöht sich bei jedem Beitreten/Verlassen; veraltete asynchrone Schritte brechen ab. */
  private seq = 0;
  private lastReported: ReportedVoiceState | null = null;
  private reportChain: Promise<void> = Promise.resolve();
  private syncQueued = false;
  private muteBeforeDeaf = false;
  private audioHost: HTMLElement | null = null;
  private readonly pendingUsers = new Set<string>();
  private readonly pendingChannels = new Set<string>();
  /** Serialisiert alle zustandsändernden Voice-Requests (Join/Leave) in Aufrufreihenfolge (S5). */
  private serverOps: Promise<unknown> = Promise.resolve();
  /** Kürzlich per PARTICIPANT_REMOVED entfernte Sitzung, für die noch ein Socket-Update erwartet wird (B1). */
  private removed: { channelId: string; serverId: string | null; timer: ReturnType<typeof setTimeout> } | null = null;

  constructor(private readonly deps: VoiceControllerDeps) {
    const storage = deps.storage();
    const preset = loadScreenPreset(storage);
    useVoiceStore.setState({
      volumes: loadVolumes(storage),
      activeDevices: loadDevicePrefs(storage),
      ...(preset ? { screenPreset: screenPresetChoice('2160p30', preset).preset } : {}),
    });
  }

  private get state(): VoiceStoreState {
    return useVoiceStore.getState();
  }

  private set(patch: Partial<VoiceStoreState>): void {
    useVoiceStore.setState(patch);
  }

  /**
   * Hängt einen zustandsändernden Request an die Warteschlange an (synchron beim Aufruf – die Reihenfolge der Aufrufe
   * ist die Reihenfolge am Server). Fehler eines Schritts halten die folgenden nicht auf.
   */
  private serial<T>(fn: () => Promise<T>): Promise<T> {
    const run = this.serverOps.then(fn, fn);
    this.serverOps = run.catch(() => undefined);
    return run;
  }

  /** Nur für Tests/E2E: aktueller LiveKit-Raum. */
  get currentRoom(): Room | null {
    return this.room;
  }

  debug(): VoiceDebugState {
    return debugState(this.room, this.state.channelId, this.state.status);
  }

  // ---------- Beitreten / Verlassen ----------

  /**
   * Beitreten bzw. Kanal wechseln. Join-/Leave-Requests laufen über eine Warteschlange (S5): Ein neuer Join wartet, bis
   * der laufende Request beantwortet ist; überholte Joins (inzwischen anderer Kanal gewünscht oder verlassen) senden
   * gar nicht erst. Da die Warteschlange die Aufrufreihenfolge einhält, entspricht der Serverzustand am Ende immer
   * dem zuletzt angeforderten Kanal (bzw. „verlassen") – nur dieser wird mit LiveKit verbunden.
   */
  async join(serverId: string | null, channelId: string): Promise<void> {
    const s = this.state;
    const pendingRemoval = this.removed !== null;
    this.clearRemoved();
    if (!pendingRemoval && s.channelId === channelId && s.status !== 'idle') return;
    const seq = ++this.seq;
    const old = this.room;
    // Kanalwechsel: alte LiveKit-Verbindung trennen; der neue Join verschiebt den VoiceState serverseitig.
    this.teardownRoom();
    this.set({ ...connectionResetPatch(), status: 'connecting', serverId, channelId, error: null });
    void this.loadChannelName(channelId);
    const request = this.serial(async () => {
      if (seq !== this.seq) return null; // überholt – der neuere Schritt bestimmt den Serverzustand
      return this.deps.request<VoiceJoinResponseDto>(`/voice/channels/${channelId}/join`, {
        method: 'POST',
        json: { selfMute: this.state.selfMute, selfDeaf: this.state.selfDeaf },
      });
    });
    if (old) await old.disconnect().catch(() => undefined);

    let res: VoiceJoinResponseDto | null;
    try {
      res = await request;
    } catch (err) {
      if (seq !== this.seq) return;
      console.warn('[voice] Beitreten fehlgeschlagen', err);
      this.set({ ...connectionResetPatch(), error: joinErrorMessage(err) });
      return;
    }
    // Überholt: Der spätere Join/Leave steht bereits hinter uns in der Warteschlange und korrigiert den Server.
    if (!res || seq !== this.seq) return;

    let room: Room | null = null;
    try {
      const storage = this.deps.storage();
      const { preset } = screenPresetChoice(res.screenshareMaxPreset, loadScreenPreset(storage) ?? this.state.screenPreset);
      room = this.deps.createRoom(buildRoomOptions(res, loadDevicePrefs(storage), preset));
      this.room = room;
      this.attachRoomListeners(room);
      this.lastReported = {
        selfMute: res.voiceState?.selfMute ?? this.state.selfMute,
        selfDeaf: res.voiceState?.selfDeaf ?? this.state.selfDeaf,
        video: res.voiceState?.video ?? false,
        streaming: res.voiceState?.streaming ?? false,
      };
      this.set({
        serverId: res.serverId ?? serverId,
        session: {
          canPublishAudio: res.canPublishAudio,
          canPublishVideo: res.canPublishVideo,
          canScreenshare: res.canScreenshare,
          bitrate: res.bitrate,
          screenshareMaxPreset: res.screenshareMaxPreset,
          userLimit: res.userLimit ?? 0,
          maxAudioBitrate: res.maxAudioBitrate ?? res.bitrate,
        },
        screenPreset: preset,
      });
      if (res.voiceState) this.set({ channelStates: applyVoiceStateUpdate(this.state.channelStates, res.voiceState) });
      this.ownStateChanged();

      const rtcConfig = rtcConfigFrom(res.iceServers);
      const connectOpts: RoomConnectOptions = { autoSubscribe: true, ...(rtcConfig ? { rtcConfig } : {}) };
      await room.connect(res.url, res.token, connectOpts);
      if (seq !== this.seq || this.room !== room) return;
      this.set({ status: 'connected' });
      // Rechte können sich seit der Join-Antwort geändert haben (ParticipantPermissionsChanged während connect).
      this.applyPermissions(room, false);
      this.scheduleSync();

      if (this.state.session?.canPublishAudio && !this.state.selfMute) await this.setMicEnabled(true);
      this.applyVolumes();
      room.startAudio().catch(() => undefined);
      this.scheduleSync();
    } catch (err) {
      if (seq !== this.seq) return;
      console.warn('[voice] Beitreten fehlgeschlagen', err);
      this.teardownRoom();
      // Synchron einreihen, bevor ein späterer Join drankommt; der Server hat uns bereits eingetragen.
      const leave = this.serial(() => this.deps.request('/voice/leave', { method: 'POST' }));
      if (room) await room.disconnect().catch(() => undefined);
      this.set({ ...connectionResetPatch(), error: joinErrorMessage(err) });
      await leave.catch(() => undefined);
    }
  }

  async leave(): Promise<void> {
    this.seq++;
    this.clearRemoved();
    const room = this.room;
    this.teardownRoom();
    this.set({ ...connectionResetPatch(), error: null });
    // Hinter einem evtl. laufenden Join einreihen – sonst könnte der Join nach dem Leave beim Server ankommen.
    const request = this.serial(() => this.deps.request('/voice/leave', { method: 'POST' }));
    if (room) await room.disconnect().catch(() => undefined);
    await request.catch((err: unknown) => console.warn('[voice] Verlassen fehlgeschlagen', err));
  }

  private clearRemoved(): void {
    if (this.removed) clearTimeout(this.removed.timer);
    this.removed = null;
  }

  private teardownRoom(): void {
    const room = this.room;
    this.room = null;
    this.lastReported = null;
    if (room) room.removeAllListeners();
    if (this.audioHost) this.audioHost.replaceChildren();
  }

  // ---------- Bedienelemente ----------

  async toggleMute(): Promise<void> {
    const s = this.state;
    const mute = !s.selfMute;
    // Wer sich entstummt, hört auch wieder (wie gewohnt).
    if (!mute && s.selfDeaf) this.set({ selfDeaf: false });
    this.set({ selfMute: mute });
    this.applyVolumes();
    if (this.room && this.state.session?.canPublishAudio) await this.setMicEnabled(!mute);
    this.scheduleSync();
  }

  async toggleDeaf(): Promise<void> {
    const s = this.state;
    const deaf = !s.selfDeaf;
    if (deaf) {
      this.muteBeforeDeaf = s.selfMute;
      this.set({ selfDeaf: true, selfMute: true });
    } else {
      this.set({ selfDeaf: false, selfMute: this.muteBeforeDeaf });
    }
    this.applyVolumes();
    if (this.room && this.state.session?.canPublishAudio) await this.setMicEnabled(!this.state.selfMute);
    this.scheduleSync();
  }

  private async setMicEnabled(enabled: boolean): Promise<void> {
    const room = this.room;
    if (!room) return;
    if (room.localParticipant.isMicrophoneEnabled === enabled) return;
    this.set({ busy: { ...this.state.busy, mic: true } });
    try {
      await room.localParticipant.setMicrophoneEnabled(enabled);
    } catch (err) {
      console.warn('[voice] Mikrofon', err);
      if (enabled) this.set({ selfMute: true, error: 'Kein Zugriff auf das Mikrofon – bitte im Browser erlauben.' });
    } finally {
      this.set({ busy: { ...this.state.busy, mic: false } });
      this.scheduleSync();
    }
  }

  async toggleCamera(): Promise<void> {
    const room = this.room;
    if (!room || !this.state.session?.canPublishVideo || this.state.busy.camera) return;
    const enable = !room.localParticipant.isCameraEnabled;
    this.set({ busy: { ...this.state.busy, camera: true } });
    try {
      await room.localParticipant.setCameraEnabled(enable);
    } catch (err) {
      console.warn('[voice] Kamera', err);
      this.set({ error: isUserCancel(err) ? 'Zugriff auf die Kamera wurde verweigert.' : 'Die Kamera konnte nicht gestartet werden.' });
    } finally {
      this.set({ busy: { ...this.state.busy, camera: false } });
      this.scheduleSync();
    }
  }

  async toggleScreen(): Promise<void> {
    const room = this.room;
    if (!room || !this.state.session?.canScreenshare || this.state.busy.screen) return;
    this.set({ busy: { ...this.state.busy, screen: true } });
    try {
      if (room.localParticipant.isScreenShareEnabled) await room.localParticipant.setScreenShareEnabled(false);
      else await this.startScreen(room);
    } finally {
      this.set({ busy: { ...this.state.busy, screen: false } });
      this.scheduleSync();
    }
  }

  /**
   * Darf Bildschirmton veröffentlicht werden? Aus den LiveKit-Rechten (Quelle screen_share_audio; fehlt bei
   * Server-Mute/-Deaf), ohne Rechte-Objekt aus dem eigenen VoiceState.
   */
  private screenAudioAllowed(room: Room): boolean {
    const rights = publishRightsFrom(room.localParticipant.permissions);
    if (rights) return rights.canScreenshareAudio;
    return !this.state.serverMute && !this.state.serverDeaf;
  }

  private async startScreen(room: Room): Promise<void> {
    const preset = this.state.screenPreset;
    const bitrate = this.state.session?.bitrate ?? 128_000;
    const publish = screenSharePublishOptions(preset, bitrate, this.state.session?.maxAudioBitrate);
    // Ohne Recht auf Bildschirmton (Server-Mute/-Deaf) gleich ohne Ton teilen.
    const withAudio = this.screenAudioAllowed(room);
    try {
      await room.localParticipant.setScreenShareEnabled(true, screenShareCaptureOptions(preset, withAudio), publish);
    } catch (err) {
      if (isUserCancel(err)) return;
      if (!withAudio) {
        console.warn('[voice] Bildschirm teilen', err);
        this.set({ error: 'Bildschirm teilen ist fehlgeschlagen.' });
        return;
      }
      // Manche Browser/Systeme lehnen Systemaudio ab → ohne Ton erneut versuchen.
      try {
        await room.localParticipant.setScreenShareEnabled(true, screenShareCaptureOptions(preset, false), publish);
      } catch (err2) {
        if (isUserCancel(err2)) return;
        console.warn('[voice] Bildschirm teilen', err2);
        this.set({ error: 'Bildschirm teilen ist fehlgeschlagen.' });
      }
    }
  }

  async setScreenPreset(wanted: string): Promise<void> {
    const max = this.state.session?.screenshareMaxPreset ?? '2160p30';
    const { preset } = screenPresetChoice(max, wanted);
    saveScreenPreset(preset, this.deps.storage());
    this.set({ screenPreset: preset });
    const room = this.room;
    if (room?.localParticipant.isScreenShareEnabled && !this.state.busy.screen) {
      // Laufende Übertragung mit neuer Qualität neu starten.
      this.set({ busy: { ...this.state.busy, screen: true } });
      try {
        await room.localParticipant.setScreenShareEnabled(false);
        await this.startScreen(room);
      } finally {
        this.set({ busy: { ...this.state.busy, screen: false } });
        this.scheduleSync();
      }
    }
  }

  setVolume(userId: string, volume: number): void {
    const volumes = saveVolume(userId, clampVolume(volume), this.deps.storage());
    this.set({ volumes });
    this.applyVolumes();
  }

  /** Eigener VoiceState im aktuellen Kanal (Server-Mute/-Deaf). */
  private ownState(): VoiceStateDto | null {
    return ownVoiceState(this.state.channelStates, this.state.channelId, this.deps.selfId());
  }

  /** Wiedergabe stumm, wenn selbst ODER vom Server taubgeschaltet (B3). */
  private isPlaybackMuted(): boolean {
    return playbackMuted(this.state.selfDeaf, this.ownState());
  }

  /** Nach jeder Änderung des eigenen VoiceStates: Server-Flags in den Store, Lautstärken neu anwenden. */
  private ownStateChanged(): void {
    const own = this.ownState();
    const serverMute = !!own?.serverMute;
    const serverDeaf = !!own?.serverDeaf;
    if (serverMute !== this.state.serverMute || serverDeaf !== this.state.serverDeaf) this.set({ serverMute, serverDeaf });
    this.applyVolumes();
  }

  /** Lautstärken (bzw. Taub = 0) auf alle Remote-Teilnehmer anwenden – Mikrofon und Bildschirmton. */
  private applyVolumes(): void {
    const room = this.room;
    if (!room) return;
    for (const p of room.remoteParticipants.values()) this.applyVolume(p);
  }

  private applyVolume(p: RemoteParticipant): void {
    const v = this.isPlaybackMuted() ? 0 : (this.state.volumes[p.identity] ?? 1);
    p.setVolume(v, Track.Source.Microphone);
    p.setVolume(v, Track.Source.ScreenShareAudio);
  }

  async startAudio(): Promise<void> {
    await this.room?.startAudio().catch(() => undefined);
    this.scheduleSync();
  }

  clearError(): void {
    this.set({ error: null });
  }

  // ---------- Geräte ----------

  async refreshDevices(): Promise<void> {
    const kinds: VoiceDeviceKind[] = ['audioinput', 'videoinput', 'audiooutput'];
    const devices = { ...this.state.devices };
    const active = { ...loadDevicePrefs(this.deps.storage()) };
    await Promise.all(
      kinds.map(async (kind) => {
        try {
          const list = await this.deps.listDevices(kind);
          devices[kind] = list
            .filter((d) => d.deviceId)
            .map((d, i): DeviceOption => ({ deviceId: d.deviceId, label: d.label || `${DEVICE_FALLBACK_LABEL[kind]} ${i + 1}` }));
        } catch {
          devices[kind] = [];
        }
        const current = this.room?.getActiveDevice(kind);
        if (current) active[kind] = current;
      }),
    );
    this.set({ devices, activeDevices: active });
  }

  async switchDevice(kind: VoiceDeviceKind, deviceId: string): Promise<void> {
    const prefs = saveDevicePref(kind, deviceId, this.deps.storage());
    this.set({ activeDevices: { ...this.state.activeDevices, ...prefs } });
    if (!this.room) return;
    try {
      await this.room.switchActiveDevice(kind, deviceId);
    } catch (err) {
      console.warn('[voice] Gerätewechsel', err);
      this.set({ error: 'Das Gerät konnte nicht gewechselt werden.' });
    }
  }

  // ---------- Kanal-/Nutzerdaten (Vorschau ohne Verbindung) ----------

  async loadChannelStates(channelId: string): Promise<void> {
    try {
      const states = await this.deps.request<VoiceStateDto[]>(`/voice/channels/${channelId}/states`);
      this.set({ channelStates: { ...this.state.channelStates, [channelId]: states } });
      for (const st of states) void this.loadUser(st.userId);
      if (channelId === this.state.channelId) {
        this.ownStateChanged();
        this.scheduleSync();
      }
    } catch {
      // Vorschau ist optional.
    }
  }

  async loadChannelName(channelId: string): Promise<void> {
    if (this.state.channelNames[channelId] || this.pendingChannels.has(channelId)) return;
    this.pendingChannels.add(channelId);
    try {
      const ch = await this.deps.request<ChannelDto>(`/channels/${channelId}`);
      if (ch?.name) this.set({ channelNames: { ...this.state.channelNames, [channelId]: ch.name } });
    } catch {
      // Name ist nur Anzeige.
    } finally {
      this.pendingChannels.delete(channelId);
    }
  }

  async loadUser(userId: string): Promise<void> {
    if (this.state.users[userId] || this.pendingUsers.has(userId)) return;
    const me = useAuthStore.getState().user as CurrentUserDto | null;
    if (me && me.id === userId) {
      this.set({ users: { ...this.state.users, [userId]: { name: me.displayName || me.username, avatarUrl: me.avatarUrl } } });
      return;
    }
    this.pendingUsers.add(userId);
    try {
      const u = await this.deps.request<UserDto>(`/users/${userId}`);
      this.set({ users: { ...this.state.users, [userId]: { name: u.displayName || u.username || shortUserLabel(userId), avatarUrl: u.avatarUrl } } });
    } catch {
      // Fallback-Label in der UI.
    } finally {
      this.pendingUsers.delete(userId);
    }
  }

  /**
   * Socket `VOICE_STATE_UPDATE` (über bindVoiceSocket).
   *
   * Eigene Updates mit anderem Kanal (B1): Nur ein Verschieben durch einen Moderator (`moved: true`,
   * `leftChannelId` = bisheriger Kanal) führt zum automatischen Beitritt in den Zielkanal (frisches Token mit den
   * Rechten des Zielkanals, docs: VoiceService „Verschieben"). Das Update wird auch dann noch ausgewertet, wenn LiveKit
   * die Sitzung kurz vorher per PARTICIPANT_REMOVED beendet hat (siehe MOVE_GRACE_MS). Ohne `moved` ist derselbe
   * Nutzer auf einem anderen Gerät/Tab beigetreten → nur lokal trennen (sonst nähme dieser Client die Sitzung zurück).
   */
  handleStateUpdate(update: VoiceStateUpdate): void {
    this.set({ channelStates: applyVoiceStateUpdate(this.state.channelStates, update) });
    if (update.channelId) void this.loadUser(update.userId);
    const me = this.deps.selfId();
    if (!me || update.userId !== me) {
      this.scheduleSync();
      return;
    }
    const removed = this.removed;
    if (removed && !this.room) {
      if (update.channelId === removed.channelId) return; // Echo des alten Kanals – weiter auf den Umzug warten
      this.clearRemoved();
      this.followServerChange(update, removed.channelId, removed.serverId);
      return;
    }
    const s = this.state;
    if (!this.room || s.status === 'connecting') {
      this.scheduleSync();
      return;
    }
    if (update.channelId !== s.channelId) {
      this.seq++;
      const room = this.room;
      this.teardownRoom();
      void room.disconnect().catch(() => undefined);
      this.followServerChange(update, s.channelId, s.serverId);
      return;
    }
    this.ownStateChanged();
    this.scheduleSync();
  }

  /** Moderator-Move (`moved: true`) → Zielkanal beitreten; sonst (getrennt/anderswo beigetreten) nur lokal beenden. */
  private followServerChange(update: VoiceStateUpdate, current: string | null, serverId: string | null): void {
    const target = voiceMoveTarget(update, current);
    if (target) {
      this.set({ ...connectionResetPatch(), error: null });
      void this.join(update.serverId ?? serverId, target);
      return;
    }
    this.set({
      ...connectionResetPatch(),
      error: update.channelId ? JOINED_ELSEWHERE_MESSAGE : 'Du wurdest aus dem Sprachkanal getrennt.',
    });
  }

  /**
   * LiveKit hat die Sitzung entfernt (PARTICIPANT_REMOVED): kurz („Verbindung wird wiederhergestellt …") auf das eigene
   * VOICE_STATE_UPDATE warten. Kommt keins, ist das Trennen endgültig – nur lokal: KEIN `POST /voice/leave`, denn die
   * Sitzung kann inzwischen einem anderen Gerät gehören (dessen Verbindung würde sonst beendet). Einen verwaisten
   * VoiceState räumt der Server selbst auf (participant_left-Webhook bzw. periodischer Abgleich).
   */
  private awaitServerUpdate(channelId: string, serverId: string | null): void {
    this.clearRemoved();
    const timer = setTimeout(() => {
      if (this.removed?.timer !== timer) return;
      this.removed = null;
      this.set({ ...connectionResetPatch(), error: disconnectMessage(DisconnectReason.PARTICIPANT_REMOVED) });
    }, MOVE_GRACE_MS);
    this.removed = { channelId, serverId, timer };
    this.set({ ...connectionResetPatch(), status: 'reconnecting', channelId, serverId, error: null });
  }

  /** READY.voiceStates o. Ä. übernehmen. */
  seedStates(states: VoiceStateDto[]): void {
    let byChannel: Record<string, VoiceStateDto[]> = {};
    for (const st of states) byChannel = applyVoiceStateUpdate(byChannel, st);
    this.set({ channelStates: byChannel });
  }

  // ---------- LiveKit-Ereignisse → Store ----------

  private attachRoomListeners(room: Room): void {
    const sync = () => this.scheduleSync();
    for (const ev of [
      RoomEvent.ParticipantDisconnected,
      RoomEvent.TrackPublished,
      RoomEvent.TrackUnpublished,
      RoomEvent.TrackMuted,
      RoomEvent.TrackUnmuted,
      RoomEvent.LocalTrackPublished,
      RoomEvent.LocalTrackUnpublished,
      RoomEvent.ActiveSpeakersChanged,
      RoomEvent.ParticipantMetadataChanged,
      RoomEvent.ParticipantNameChanged,
      RoomEvent.ConnectionQualityChanged,
      RoomEvent.TrackStreamStateChanged,
      RoomEvent.AudioPlaybackStatusChanged,
      RoomEvent.ActiveDeviceChanged,
    ] as const) {
      room.on(ev, sync);
    }
    room.on(RoomEvent.ParticipantConnected, (p: RemoteParticipant) => {
      this.applyVolume(p);
      sync();
    });
    room.on(RoomEvent.ParticipantPermissionsChanged, (_prev: unknown, p: Participant) => {
      if (this.room !== room || p !== (room.localParticipant as Participant)) return;
      this.applyPermissions(room, this.state.status !== 'connecting');
    });
    room.on(RoomEvent.TrackSubscribed, (track: RemoteTrack, _pub, participant: RemoteParticipant) => {
      if (track.kind === Track.Kind.Audio) {
        const el = track.attach();
        el.setAttribute('data-user-id', participant.identity);
        this.ensureAudioHost().appendChild(el);
        this.applyVolume(participant);
      }
      sync();
    });
    room.on(RoomEvent.TrackUnsubscribed, (track: RemoteTrack) => {
      if (track.kind === Track.Kind.Audio) for (const el of track.detach()) el.remove();
      sync();
    });
    room.on(RoomEvent.MediaDevicesChanged, () => {
      void this.refreshDevices();
    });
    room.on(RoomEvent.Reconnecting, () => {
      if (this.room === room) this.set({ status: 'reconnecting' });
    });
    room.on(RoomEvent.SignalReconnecting, () => {
      if (this.room === room) this.set({ status: 'reconnecting' });
    });
    room.on(RoomEvent.Reconnected, () => {
      if (this.room !== room) return;
      this.set({ status: 'connected' });
      sync();
    });
    room.on(RoomEvent.Disconnected, (reason?: DisconnectReason) => {
      if (this.room !== room) return; // eigenes Verlassen/Wechseln
      const { channelId, serverId } = this.state;
      this.seq++;
      this.teardownRoom();
      // Verschieben durch Moderator: LiveKit entfernt die Sitzung meist vor dem Socket-Update → kurz darauf warten.
      if (reason === DisconnectReason.PARTICIPANT_REMOVED && channelId) this.awaitServerUpdate(channelId, serverId);
      else this.set({ ...connectionResetPatch(), error: disconnectMessage(reason) });
    });
  }

  /**
   * Veröffentlichungsrechte aus `localParticipant.permissions` übernehmen (B4; Server-Mute, Rechteänderungen).
   * Entzogene Quellen werden lokal beendet; wird das Mikrofon wieder freigegeben und ist man nicht selbst stumm, wird es
   * eingeschaltet. `withMedia=false`: nur Rechte übernehmen (während des Verbindens schaltet join() selbst).
   */
  private applyPermissions(room: Room, withMedia: boolean): void {
    const rights = publishRightsFrom(room.localParticipant.permissions);
    const session = this.state.session;
    if (!rights || !session) return;
    const { canPublishAudio, canPublishVideo, canScreenshare } = rights;
    const changed = canPublishAudio !== session.canPublishAudio || canPublishVideo !== session.canPublishVideo || canScreenshare !== session.canScreenshare;
    if (changed) this.set({ session: { ...session, canPublishAudio, canPublishVideo, canScreenshare } });
    if (withMedia) void this.enforcePublishRights(room);
  }

  private async enforcePublishRights(room: Room): Promise<void> {
    const session = this.state.session;
    if (!session || this.room !== room) return;
    const lp: LocalParticipant = room.localParticipant;
    if (!session.canPublishAudio) await this.setMicEnabled(false);
    else if (!this.state.selfMute) await this.setMicEnabled(true);
    if (!session.canPublishVideo && lp.isCameraEnabled) {
      await lp.setCameraEnabled(false).catch((err: unknown) => console.warn('[voice] Kamera stoppen', err));
    }
    if (!session.canScreenshare && lp.isScreenShareEnabled) {
      await lp.setScreenShareEnabled(false).catch((err: unknown) => console.warn('[voice] Bildschirm stoppen', err));
    }
    // Server-Mute/-Deaf während des Teilens: Bild weiter senden, nur den Bildschirmton beenden.
    const screenAudio = lp.getTrackPublication(Track.Source.ScreenShareAudio)?.track;
    if (screenAudio && !this.screenAudioAllowed(room)) {
      await lp.unpublishTrack(screenAudio).catch((err: unknown) => console.warn('[voice] Bildschirmton stoppen', err));
    }
    this.scheduleSync();
  }

  private ensureAudioHost(): HTMLElement {
    if (!this.audioHost || !this.audioHost.isConnected) {
      const host = document.createElement('div');
      host.setAttribute('data-testid', 'voice-remote-audio');
      host.setAttribute('aria-hidden', 'true');
      host.style.display = 'none';
      document.body.appendChild(host);
      this.audioHost = host;
    }
    return this.audioHost;
  }

  /** Mehrere Ereignisse in einem Durchlauf zusammenfassen. */
  scheduleSync(): void {
    if (this.syncQueued) return;
    this.syncQueued = true;
    queueMicrotask(() => {
      this.syncQueued = false;
      this.sync();
    });
  }

  /** Raum → Store; meldet geänderten eigenen Status an `PATCH /voice/state`. */
  sync(): void {
    const room = this.room;
    if (!room) return;
    const s = this.state;
    const states = s.channelId ? (s.channelStates[s.channelId] ?? []) : [];
    const isDeafened = (userId: string, isLocal: boolean) => {
      if (isLocal) return this.isPlaybackMuted();
      const st = states.find((x) => x.userId === userId);
      return !!st && (st.selfDeaf || st.serverDeaf);
    };
    const lp = room.localParticipant;
    this.set({
      participants: snapshotRoom(room, isDeafened),
      camera: lp.isCameraEnabled,
      screen: lp.isScreenShareEnabled,
      quality: qualityLevel(lp.connectionQuality),
      canPlaybackAudio: room.canPlaybackAudio,
    });
    this.report();
  }

  private report(): void {
    this.reportChain = this.reportChain.then(() => this.flushReport());
  }

  /** Wartet auf alle ausstehenden Statusmeldungen (Tests). */
  flushReports(): Promise<void> {
    return this.reportChain;
  }

  private async flushReport(): Promise<void> {
    const room = this.room;
    if (!room || this.state.status === 'connecting') return;
    const s = this.state;
    const next: ReportedVoiceState = { selfMute: s.selfMute, selfDeaf: s.selfDeaf, video: s.camera, streaming: s.screen };
    const patch = diffVoiceState(this.lastReported, next);
    if (!patch) return;
    try {
      const dto = await this.deps.request<VoiceStateDto>('/voice/state', { method: 'PATCH', json: patch });
      // Erst nach Erfolg als gemeldet merken (O4); bei Fehler sendet der nächste Sync erneut.
      if (this.room === room) this.lastReported = next;
      if (dto?.userId) {
        this.set({ channelStates: applyVoiceStateUpdate(this.state.channelStates, dto) });
        if (this.room === room) this.ownStateChanged();
      }
    } catch (err) {
      console.warn('[voice] Status melden fehlgeschlagen', err);
    }
  }
}

const defaultDeps: VoiceControllerDeps = {
  request: (path, init) => api(path, init),
  createRoom: (options) => new Room(options),
  listDevices: (kind) => Room.getLocalDevices(kind, false),
  storage: safeStorage,
  selfId: () => useAuthStore.getState().user?.id ?? null,
};

let instance: VoiceController | null = null;

/** Die Instanz des Tabs (lazy, damit Tests eigene Controller bauen können). */
export function getVoiceController(): VoiceController {
  // E2E-Hook `window.__lfVoice`: debug-hook.ts (nur Dev/VITE_E2E=1).
  instance ??= new VoiceController(defaultDeps);
  return instance;
}

/** Für Tests: eigene Instanz einsetzen bzw. mit null zurücksetzen. */
export function setVoiceController(controller: VoiceController | null): void {
  instance = controller;
}
